import React, { useEffect, useMemo, useState } from 'react'
import Back from '../../features/back/Back'
import { raffleContent } from '../../../constants/copyright'
import { useRequests } from '../../../redux/reducer/requests'
import useStateControls from '../../../hooks/useStateControls'
import { image } from '../../../utils/data/baseUrl'
import { useUser } from '../../../redux/reducer/user'
import { useContent, useDrawing } from '../../../redux/reducer/content'
import { useGetPrizeFromQuest } from '../../../hooks/useGetPrizeFromQuest'
import AppLoader from '../../features/appLoader/AppLoader'
import { useTranslation, defaultStore } from '../../../translations'
import classNames from 'classnames'
import { safeHTML } from '../../../utils/safeHTML'

const Raffle = React.forwardRef(function Raffle({ callbacks }, ref) {
  const { t, i18n } = useTranslation()
  const {
    authData: { isWeb },
  } = useRequests()
  const { baseTitle, loseTitle, winTitle, links } = raffleContent
  const { raffle } = useContent()
  const { winnersPrizes } = useDrawing()
  const [isWin, setIsWin] = useState(false)

  const { profile } = useUser()
  const { setState } = useStateControls()
  const isPending = useGetPrizeFromQuest({ callbacks })

  const goToMain = () => {
    setState('main')
  }

  useEffect(() => {
    if (raffle && raffle?.is_finish) {
      callbacks.getDrawingResultWinners({ page: 1 })
    }
  }, [raffle])

  useEffect(() => {
    const isWinner = winnersPrizes?.[1]?.some((item) => item.userUid === profile.uid) || false

    if (raffle && raffle?.is_finish && isWinner) {
      setIsWin(true)
    }
  }, [winnersPrizes, raffle, profile])

  const formatWithSpaces = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

  const raffleInfo = useMemo(() => {
    if (!raffle || !profile) {
      return {
        title: baseTitle,
        description: t('raffle.about.title'),
        buttonText: t('raffle.about.part'),
        takePart: false,
        currentScore: '0/0',
      }
    }

    const { min_score, is_finish } = raffle
    const { raffle_exp } = profile
    const minScoreFormated = formatWithSpaces(min_score)
    const userScoreFormated = formatWithSpaces(raffle_exp)

    if (!is_finish && raffle_exp >= min_score) {
      return {
        title: baseTitle,
        description: t('raffle.about.title'),
        buttonText: t('raffle.about.part'),
        takePart: true,
        currentScore: `${minScoreFormated}/${minScoreFormated}`,
      }
    }

    if (!is_finish && raffle_exp < min_score) {
      return {
        title: baseTitle,
        description: t('raffle.about.title'),
        buttonText: t('raffle.about.noPart'),
        takePart: false,
        currentScore:
          i18n.language === 'ar'
            ? `${minScoreFormated}/${userScoreFormated}`
            : `${userScoreFormated}/${minScoreFormated}`,
      }
    }

    if (is_finish && isWin) {
      return {
        title: winTitle,
        description: t('raffle.finish.win', { href: links[defaultStore] }),
        winners: t('raffle.finish.otherWinners'),
      }
    }

    if (is_finish && !isWin) {
      return {
        title: loseTitle,
        buttonText: t('other.continue'),
        description: t('raffle.finish.lose'),
        winners: t('raffle.finish.winners'),
      }
    }
  }, [profile, raffle, isWin])

  return (
    <section
      className={`raffle ${isWeb ? '' : 'app'}`}
      ref={ref}
    >
      <div className={`raffle__header`}>
        <Back onClick={() => goToMain()} />

        <div className={`raffle__header-container`}>
          <h1 className={classNames('raffle__header-title', { finish: raffle?.is_finish })}>
            {t(raffleInfo.title)}
          </h1>
        </div>
      </div>

      <div className={'raffle__content'}>
        <div className={'raffle__content-container'}>
          <span className={'raffle__content-title'}>{safeHTML(raffleInfo.description)}</span>

          {!raffle?.is_finish ? (
            <>
              <div className={'raffle__info'}>
                <img
                  src={image('raffle/xp.png')}
                  alt='xp image'
                  className={'image-raffle'}
                />

                <div className={'content-raffle'}>
                  <div className={'info-raffle'}>
                    <div className={'title-raffle'}>
                      <div>{t('raffle.about.element.title')}</div>
                      <div
                        className={'title-score'}
                        dir='ltr'
                      >
                        {raffleInfo.currentScore}
                      </div>
                    </div>
                    <span className={'subtitle-raffle'}>{t('raffle.about.element.subtitle')}</span>
                  </div>

                  <button
                    type='button'
                    className={classNames('raffle-button', { active: raffleInfo.takePart })}
                  >
                    {raffleInfo.buttonText}
                  </button>
                </div>
              </div>

              <div className={'raffle__bottom'}>
                <span className={'raffle__prize-title'}>{t('raffle.prizes')}</span>

                <div className={'raffle__prize'}>
                  <img
                    src={image('raffle/box.png')}
                    alt='box image'
                    className={'raffle__prize-box'}
                  />

                  <div>
                    <div className={'raffle__prize-name'}>{safeHTML(t('raffle.about.prize'))}</div>
                    <div className={'raffle__prize-count'}>{t('raffle.about.prizesCount')}</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className={'raffle__finish'}>
              {!isWin && (
                <button
                  className={'raffle__finish-button'}
                  onClick={() => goToMain()}
                >
                  {raffleInfo.buttonText}
                </button>
              )}

              <div className={classNames('raffle__finish-prize', { win: isWin })}>
                <img
                  src={image('raffle/box.png')}
                  alt='box image'
                  className={'raffle__finish-box'}
                />

                <span className={'raffle__finish-name'}>{t('raffle.finish.boxPrize')}</span>
              </div>

              {isWin && <span className={'raffle__finish-id'}>{t('raffle.finish.id')}</span>}

              <div className={'raffle__winners'}>
                <span className={'raffle__winners-title'}>{raffleInfo.winners}</span>

                <ul className={'raffle__winners-list'}>
                  {winnersPrizes[1]?.map((winner) => (
                    <li className={'raffle__winners-item'}>
                      <div className={'raffle__winners-info'}>
                        <div className={'raffle__winners-id'}>{`ID${winner.userUid}`}</div>

                        <div className={'raffle__winners-value'}>
                          {safeHTML(t('raffle.about.prize'))}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {isPending && <AppLoader />}
    </section>
  )
})

export default Raffle
